import { FaCaretDown, FaCaretUp } from 'react-icons/fa'
import { MdEdit, MdOutlineDelete } from 'react-icons/md'
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'

import {
  Box,
  Center,
  Icon,
  IconButton,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'

import * as api from '../../../../api/imprensaOficial'

interface ImprensaOficial {
  seqConteudo: number
  nomePublicacao: string
  dscSituacaoPublicacao: string
  datRegistro: string
  datPublicacao: string
  datDespublicar: string
}

interface TabelaImprensaOficialProps {
  listaImprensaOficial: ImprensaOficial[]
  listaVazia: string
  loading: boolean
  onDelete: (seqMidia: number) => void
}

type Ordenacao =
  | { chave: keyof ImprensaOficial; direcao: 'asc' | 'desc' }
  | null

const TabelaImprensaOficial = ({
  listaImprensaOficial,
  listaVazia,
  loading,
  onDelete
}: TabelaImprensaOficialProps) => {
  const navigate = useNavigate()
  const [ordenacao, setOrdenacao] = useState<Ordenacao>(null)
  const [colunaDestacada, setColunaDestacada] = useState<
    keyof ImprensaOficial | null
  >(null)

  function deletarPublicacao(seqPublicacao: number) {
    Swal.fire({
      title: 'Tem certeza?',
      text: 'Certeza que deseja deletar esta publicação?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1789FC',
      cancelButtonColor: '#EDF2F7',
      confirmButtonText: 'Sim, deletar',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: 'info',
          title: 'Aguarde',
          allowOutsideClick: false,
          text: 'Deletando...',
          confirmButtonColor: '#1789FC',
          confirmButtonText: 'OK'
        })
        try {
          const obterImprensaOficial = await api.obterImprensaOficial(
            seqPublicacao
          )
          if (obterImprensaOficial.binPublicacao) {
            const delimiter = '/cms-dev-files/'
            const index = obterImprensaOficial.binPublicacao.lastIndexOf(
              delimiter
            )
            const stringFinalDaUrlCdn =
              index !== -1
                ? obterImprensaOficial.binPublicacao.substring(
                    index + delimiter.length
                  )
                : obterImprensaOficial.binPublicacao
            await api.deletarImprensaOficialNoCdn(stringFinalDaUrlCdn)
          }
        } catch (error) {
          console.error('Erro ao obter publicação:', error)
        }
        try {
          await api.deletarImprensaOficial(seqPublicacao).then(() => {
            onDelete(seqPublicacao)
            Swal.fire({
              icon: 'success',
              title: 'Sucesso!',
              text: 'Publicação deletada com sucesso.',
              confirmButtonColor: '#1789FC',
              confirmButtonText: 'OK'
            })
          })
        } catch (error) {
          console.error('Erro ao deletar publicação:', error)
          Swal.fire({
            icon: 'error',
            title: 'Ocorreu um erro!',
            text: 'Erro ao deletar publicação. Tente novamente mais tarde.',
            confirmButtonColor: '#1789FC',
            confirmButtonText: 'OK'
          }).then(() => {
            navigate(-1)
          })
        }
      }
    })
  }

  const manipularOrdenacao = (chave: keyof ImprensaOficial) => {
    let direcao: 'asc' | 'desc' = 'asc'
    if (ordenacao && ordenacao.chave === chave && ordenacao.direcao === 'asc') {
      direcao = 'desc'
    }
    setOrdenacao({ chave, direcao })
  }

  const listaOrdenada = useMemo(() => {
    const copia = [...listaImprensaOficial]
    if (ordenacao !== null) {
      copia.sort((a, b) => {
        const valorA = a[ordenacao.chave]
        const valorB = b[ordenacao.chave]
        if (typeof valorA === 'string' && typeof valorB === 'string') {
          const comparacao = valorA.localeCompare(valorB)
          return ordenacao.direcao === 'asc' ? comparacao : -comparacao
        }
        if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1
        if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1
        return 0
      })
    }
    return copia
  }, [listaImprensaOficial, ordenacao])

  const obterIndicadorOrdenacao = (chave: keyof ImprensaOficial) => {
    if (ordenacao && ordenacao.chave === chave) {
      return ordenacao.direcao === 'asc' ? (
        <FaCaretUp color="#1789FC" style={{ marginLeft: '4px' }} />
      ) : (
        <FaCaretDown color="#1789FC" style={{ marginLeft: '4px' }} />
      )
    }
    return <FaCaretUp color="#4a5568" style={{ marginLeft: '4px' }} />
  }

  const limitarTexto = (texto: string, tamanhoMaximo: number) => {
    return texto.length > tamanhoMaximo
      ? `${texto.substring(0, tamanhoMaximo)}...`
      : texto
  }

  const renderizarLinhas = () =>
    listaOrdenada.length > 0 ? (
      listaOrdenada.map((imprensaOficial) => (
        <Tr key={imprensaOficial.seqConteudo}>
          <Td>{limitarTexto(imprensaOficial.nomePublicacao, 30)}</Td>
          <Td>{imprensaOficial.dscSituacaoPublicacao}</Td>
          <Td>{imprensaOficial.datRegistro.split('-').reverse().join('/')}</Td>
          <Td>{imprensaOficial.datPublicacao.split('-').reverse().join('/')}</Td>
          <Td>
            {imprensaOficial.datDespublicar.split('-').reverse().join('/')}
          </Td>
          <Td>
            <IconButton
              aria-label="Botão Editar"
              bgColor="#fff"
              onClick={() =>
                navigate(
                  `editar-imprensa-oficial/${imprensaOficial.seqConteudo}`
                )
              }
            >
              <Icon as={MdEdit} fontSize="24px" color="#1789FC" />
            </IconButton>
          </Td>
          <Td>
            <IconButton
              aria-label="Botão deletar"
              bgColor="#fff"
              _hover={{ bg: '#ff00002b' }}
              onClick={() => deletarPublicacao(imprensaOficial.seqConteudo)}
            >
              <Icon as={MdOutlineDelete} fontSize="24px" color="red" />
            </IconButton>
          </Td>
        </Tr>
      ))
    ) : (
      <Tr>
        <Td colSpan={6} color="#999898" textAlign="center">
          {listaVazia}
        </Td>
      </Tr>
    )

  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('nomePublicacao')}
              onMouseEnter={() => setColunaDestacada('nomePublicacao')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box
                display="flex"
                alignItems="center"
                color={
                  ordenacao?.chave === 'nomePublicacao' ? 'blue.500' : 'inherit'
                }
              >
                Número da publicação{' '}
                {obterIndicadorOrdenacao('nomePublicacao')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('dscSituacaoPublicacao')}
              onMouseEnter={() => setColunaDestacada('dscSituacaoPublicacao')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box
                display="flex"
                alignItems="center"
                color={
                  ordenacao?.chave === 'dscSituacaoPublicacao'
                    ? 'blue.500'
                    : 'inherit'
                }
              >
                Situação {obterIndicadorOrdenacao('dscSituacaoPublicacao')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('datRegistro')}
              onMouseEnter={() => setColunaDestacada('datRegistro')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box
                display="flex"
                alignItems="center"
                color={
                  ordenacao?.chave === 'datRegistro' ? 'blue.500' : 'inherit'
                }
              >
                Data do Registro {obterIndicadorOrdenacao('datRegistro')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('datPublicacao')}
              onMouseEnter={() => setColunaDestacada('datPublicacao')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box
                display="flex"
                alignItems="center"
                color={
                  ordenacao?.chave === 'datPublicacao' ? 'blue.500' : 'inherit'
                }
              >
                Data Para Publicação {obterIndicadorOrdenacao('datPublicacao')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('datDespublicar')}
              onMouseEnter={() => setColunaDestacada('datDespublicar')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box
                display="flex"
                alignItems="center"
                color={
                  ordenacao?.chave === 'datDespublicar' ? 'blue.500' : 'inherit'
                }
              >
                Data Fim da Publicação{' '}
                {obterIndicadorOrdenacao('datDespublicar')}
              </Box>
            </Th>
            <Th>Editar</Th>
            <Th>Deletar</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr>
              <Td colSpan={6}>
                <Center>
                  <Spinner size="lg" color="blue.500" />
                </Center>
              </Td>
            </Tr>
          ) : (
            renderizarLinhas()
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default TabelaImprensaOficial;
